/* Home finder — zip + price range → AI-estimated listings with requirements, reviews, links. */
import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Loader2, Search, ExternalLink } from "lucide-react";
import { HomeListingCard } from "@/components/HomeListingCard";

export function HomeFinder({ vault, onMsg, onVaultChanged }) {
  const p = vault.search_prefs || {};
  const [zip, setZip] = useState(p.zip || "");
  const [mode, setMode] = useState(p.mode || "rent");
  const [min, setMin] = useState(p.min_price ?? "");
  const [max, setMax] = useState(p.max_price ?? "");
  const [beds, setBeds] = useState(p.beds ?? 0);
  const [income, setIncome] = useState(vault.monthly_gross_income ?? "");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (p.zip) api.get("/home-vault/search").then((r) => setRes(r.data)).catch(() => {}); }, []); // eslint-disable-line

  const search = async (e) => {
    e?.preventDefault(); setBusy(true); onMsg("");
    try {
      if (income !== "" && Number(income) !== Number(vault.monthly_gross_income)) await api.post("/home-vault/income", { monthly_gross_income: Number(income) });
      const { data } = await api.post("/home-vault/search", { zip, mode, min_price: Number(min), max_price: Number(max), beds: Number(beds) });
      setRes(data); onVaultChanged();
    } catch (err) { onMsg(formatErr(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const useTarget = async (amt) => {
    onMsg("");
    try { await api.post(`/home-vault/target?vault_id=${vault.vault_id}`, { target_amount: amt }); onMsg(`Goal set to $${amt.toLocaleString()}.`); onVaultChanged(); }
    catch (err) { onMsg(formatErr(err.response?.data?.detail) || err.message); }
  };

  const inp = "bg-white border border-sand rounded-md px-2 py-1.5 text-sm font-mono w-full";
  return (
    <div className="bg-linen border border-sand rounded-2xl p-6" data-testid="home-finder">
      <form onSubmit={search} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-sage">Zip<input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="78754" required pattern="\d{5}" className={inp} data-testid="home-finder-zip" /></label>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-sage">Rent / Buy
          <select value={mode} onChange={(e) => setMode(e.target.value)} className={inp} data-testid="home-finder-mode"><option value="rent">Rent</option><option value="buy">Buy</option></select></label>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-sage">Min {mode === "rent" ? "$/mo" : "$"}<input type="number" min="0" value={min} onChange={(e) => setMin(e.target.value)} required className={inp} data-testid="home-finder-min" /></label>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-sage">Max {mode === "rent" ? "$/mo" : "$"}<input type="number" min="1" value={max} onChange={(e) => setMax(e.target.value)} required className={inp} data-testid="home-finder-max" /></label>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-sage">Beds
          <select value={beds} onChange={(e) => setBeds(e.target.value)} className={inp} data-testid="home-finder-beds">{[0, 1, 2, 3, 4].map((b) => <option key={b} value={b}>{b === 0 ? "Any" : `${b}+`}</option>)}</select></label>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-sage">Gross income /mo<input type="number" min="0" value={income} onChange={(e) => setIncome(e.target.value)} placeholder="4500" className={inp} data-testid="home-finder-income" /></label>
        <button type="submit" disabled={busy} className="col-span-2 md:col-span-6 bg-ink text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="home-finder-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} {busy ? "Researching this zip…" : "Find homes"}
        </button>
      </form>

      {res && (
        <div className="mt-5" data-testid="home-finder-results">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-serif text-xl inline-flex items-center gap-2" data-testid="home-finder-place">{res.place} · {res.mode === "rent" ? "for rent" : "for sale"}
                {res.live && <span className="text-[9px] uppercase tracking-wider font-semibold bg-forest text-white rounded-full px-2 py-0.5" data-testid="home-finder-live-badge">Live</span>}
              </div>
              <div className="text-[11px] text-clay mt-1 max-w-lg" data-testid="home-finder-disclaimer">
                {res.live
                  ? `${res.listings.length} active listing${res.listings.length === 1 ? "" : "s"} from RentCast, refreshed daily. Requirements and reviews are typical for the area (AI-researched) — always verify with the landlord or agent before applying.`
                  : "Estimates researched by AI for this zip and budget — not live listings. Requirements and reviews are typical for the area; always verify with the landlord or agent before applying."}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap text-[11px]">
              {Object.entries(res.links).map(([k, url]) => (
                <a key={k} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-ink rounded-full px-2.5 py-1 hover:bg-ink hover:text-white capitalize" data-testid={`home-finder-link-${k}`}>{k} <ExternalLink className="w-3 h-3" /></a>
              ))}
            </div>
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {res.listings.length === 0 && <div className="col-span-2 text-sm text-sage border border-dashed border-sand rounded-xl p-5 text-center" data-testid="home-finder-empty">No active listings in {res.zip} within that budget right now. Widen the price range or try a nearby zip.</div>}
            {res.listings.map((l) => <HomeListingCard key={l.id} l={l} mode={res.mode} income={res.monthly_gross_income} onUseTarget={useTarget} />)}
          </div>
        </div>
      )}
    </div>
  );
}
